import {
  // Accordion, (SMS status and history section disabled below)
  Alert,
  Avatar,
  Badge,
  Blockquote,
  Button,
  Card,
  Group,
  Loader,
  Rating,
  Select,
  Stack,
  Stepper,
  Text,
  Textarea,
  // Timeline,
} from "@mantine/core";
import { IconCheck, IconEyeOff, IconWorld, IconX } from "@tabler/icons-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import {
  ReviewModerationDecision,
  ReviewRejectReason,
  ReviewReplyAction,
  ReviewVisibilityAction,
} from "@/models/club";
import { reviews, reviewKeys, useReviewCapabilities } from "@/services/reviews";
import {
  // auditLabel,
  consentLabels,
  hasReviewAction,
  moderationLabels,
  replyLabels,
  reviewError,
  visibilityLabels,
  type ReviewApiError,
} from "@/services/reviews/policy";
// import InvitationStatus from "@/modules/reviews/InvitationStatus";
import moment from "moment-jalaali";
import useIsSmall from "@/hooks/useIsSmall";
import { moderationColor, visibilityColor } from "./reviewStyle";

type Action = "approve" | "reject" | "draft" | "reply" | "publish" | "hide";

// Highlights the card of the step that needs action next.
const activeCard = {
  borderColor: "var(--mantine-primary-color-filled)",
  boxShadow: "0 0 0 1px var(--mantine-primary-color-filled)",
};

export default function ReviewDetail({
  reviewId,
  onDirtyChange,
}: {
  reviewId: number;
  onDirtyChange?: (dirty: boolean) => void;
}) {
  const client = useQueryClient();
  const query = useQuery({
    queryKey: [...reviewKeys.all, "detail", reviewId],
    queryFn: () => reviews.detail(reviewId),
    retry: false,
  });
  const caps = useReviewCapabilities();
  const isSmall = useIsSmall();
  const data = query.data;
  const [reply, setReply] = useState<string | null>(null);
  const [reasonCode, setReasonCode] = useState<string | null>(null);
  const [reason, setReason] = useState("");
  // Reject and hide ask for a reason only after their button is pressed.
  const [form, setForm] = useState<"reject" | "hide" | null>(null);
  const replyValue =
    reply ?? data?.reply?.draftText ?? data?.reply?.publishedText ?? "";
  useEffect(() => {
    onDirtyChange?.(reply !== null || !!reason);
  }, [reply, reason, onDirtyChange]);
  const actionAllowed = (action: Action) => {
    const names: Record<Action, string[]> = {
      approve: ["approve", "moderate.approve"],
      reject: ["reject", "moderate.reject"],
      draft: ["saveReplyDraft", "reply.saveDraft", "replyDraft", "saveDraft"],
      reply: ["publishReply", "reply.publish"],
      publish: ["publish", "visibility.publish"],
      hide: ["hide", "visibility.hide"],
    };
    return hasReviewAction(data?.allowedActions, ...names[action]);
  };
  const mutation = useMutation({
    mutationFn: async (action: Action) => {
      if (!data?.version || !actionAllowed(action))
        throw new Error("عملیات مجاز نیست");
      if (action === "approve" || action === "reject")
        return reviews.moderate(reviewId, {
          decision:
            action === "approve"
              ? ReviewModerationDecision.Approved
              : ReviewModerationDecision.Rejected,
          reasonCode:
            action === "reject"
              ? (reasonCode as ReviewRejectReason)
              : undefined,
          reasonText: action === "reject" ? reason.trim() : undefined,
          expectedVersion: data.version,
        });
      if (action === "draft" || action === "reply") {
        let replyVersion = data.reply?.version ?? null;
        // Publishing takes the saved draft; save unsaved edits first so one click publishes what is on screen.
        if (action === "draft" || reply !== null) {
          const saved = await reviews.reply(reviewId, {
            action: ReviewReplyAction.SaveDraft,
            text: replyValue.trim(),
            expectedVersion: replyVersion,
          });
          if (action === "draft") return saved;
          replyVersion = saved.replyVersion ?? null;
        }
        return reviews.reply(reviewId, {
          action: ReviewReplyAction.Publish,
          text: null,
          expectedVersion: replyVersion,
        });
      }
      return reviews.visibility(reviewId, {
        action:
          action === "publish"
            ? ReviewVisibilityAction.Publish
            : ReviewVisibilityAction.Hide,
        // Only hiding takes a reason; don't send leftover reject text with a publish.
        reason: action === "hide" ? reason.trim() || undefined : undefined,
        expectedVersion: data.version,
      });
    },
    onSuccess: (_, action) => {
      if (action === "draft" || action === "reply") setReply(null);
      setReason("");
      setReasonCode(null);
      setForm(null);
      void client.invalidateQueries({ queryKey: reviewKeys.all });
    },
    onError: () => {
      void client.invalidateQueries({ queryKey: reviewKeys.all });
    },
  });
  if (query.isLoading) return <Loader />;
  if (query.isError)
    return (
      <Alert color="red">
        {reviewError(query.error)}
        <Button size="xs" mt="xs" onClick={() => void query.refetch()}>تلاش مجدد</Button>
      </Alert>
    );
  if (!data) return null;
  const busy = mutation.isLoading || query.isFetching;
  const fieldErrors = (mutation.error as ReviewApiError | null)?.response?.data
    ?.fieldErrors;
  const publishEligible =
    data.moderationStatus === "approved" &&
    data.publicConsent === "granted" &&
    data.eligibilityStatus === "eligible";
  // Spec defaults when capabilities are unavailable, so the buttons are not disabled for good.
  const replyLimit = caps.data?.limits?.replyMaxLength || 1000;
  const reasonLimit = caps.data?.limits?.reasonMaxLength || 500;

  const moderated = data.moderationStatus === "approved" || data.moderationStatus === "rejected";
  const rejected = data.moderationStatus === "rejected";
  const published = data.visibilityStatus === "published";
  const replied = data.reply?.state === "published" || !!data.reply?.publishedText;
  const activeStep = !moderated || rejected ? 0 : !published ? 1 : !replied ? 2 : 3;
  const publishBlocker =
    data.moderationStatus !== "approved"
      ? "برای انتشار، ابتدا نظر را تأیید کنید."
      : data.publicConsent !== "granted"
        ? "مشتری اجازه نمایش عمومی نداده است؛ نظر فقط در پنل می‌ماند."
        : data.eligibilityStatus !== "eligible"
          ? "این فاکتور برای انتشار نظر واجد شرایط نیست."
          : "";
  const name =
    [data.customer?.firstName, data.customer?.lastName].filter(Boolean).join(" ") ||
    data.displayName ||
    "مشتری";
  const replyState = data.reply?.state ?? data.replyState ?? "";
  const canModerate = actionAllowed("approve") || actionAllowed("reject");
  const canVisibility = actionAllowed("publish") || actionAllowed("hide");
  const canReply = actionAllowed("draft") || actionAllowed("reply");
  const pendingAction = mutation.isLoading ? mutation.variables : undefined;
  const cancelForm = () => {
    setForm(null);
    setReason("");
    setReasonCode(null);
  };

  return (
    <Stack gap="md">
      {/* The review itself */}
      <Card withBorder radius="md" padding="md">
        <Stack gap="xs">
          <Group justify="space-between" wrap="nowrap" align="flex-start">
            <Group gap="sm" wrap="nowrap" style={{ minWidth: 0 }}>
              <Avatar radius="xl" color="myColor">{name.charAt(0)}</Avatar>
              <div style={{ minWidth: 0 }}>
                <Text fw={700} truncate>{name}</Text>
                {data.customer?.phoneNumber && (
                  <Text size="xs" c="dimmed">{data.customer.phoneNumber}</Text>
                )}
              </div>
            </Group>
            <Text size="xs" c="dimmed">
              {data.submittedAtUtc ? moment(data.submittedAtUtc).format("jYYYY/jMM/jDD · HH:mm") : ""}
            </Text>
          </Group>
          <Group gap={6}>
            <Rating readOnly value={data.visitRating ?? 0} />
            <Text size="sm" fw={600}>{(data.visitRating ?? 0).toLocaleString("fa-IR")} از ۵</Text>
            <Text size="xs" c="dimmed">امتیاز مراجعه</Text>
          </Group>
          <Blockquote color="gray" p="sm" radius="md" style={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>
            {data.text || <Text span c="dimmed">نظر بدون متن</Text>}
          </Blockquote>
          {(data.items?.length ?? 0) > 0 ? (
            <Group gap={6}>
              {data.items?.map((item) => (
                <Badge key={item.sourcePurchaseItemId} variant="outline" color="gray" tt="none" fw={500}>
                  {item.serviceTitle}
                  {item.professional?.displayName ? ` · ${item.professional.displayName}` : ""}
                </Badge>
              ))}
            </Group>
          ) : data.serviceTitles?.length ? (
            <Text size="sm" c="dimmed">خدمات: {data.serviceTitles.join("، ")}</Text>
          ) : null}
          {(data.professionalRatings?.length ?? 0) > 0 && (
            <Stack gap={4}>
              <Text size="xs" c="dimmed">امتیاز متخصص‌ها (جدا از امتیاز مراجعه)</Text>
              {data.professionalRatings?.map((person) => (
                <Group key={person.merchantUserId} gap={6}>
                  <Text size="sm" miw={110}>{person.displayName || `متخصص ${person.merchantUserId}`}</Text>
                  <Rating readOnly size="xs" value={person.rating ?? 0} />
                  {person.attributionValidity !== "valid" && (
                    <Badge size="xs" color="orange" variant="light">انتساب نیازمند بررسی</Badge>
                  )}
                </Group>
              ))}
            </Stack>
          )}
          <Group gap={6}>
            <Badge variant="light" color={data.publicConsent === "granted" ? "teal" : "gray"} tt="none" fw={500}>
              انتشار عمومی: {consentLabels[data.publicConsent ?? ""] || "نامشخص"}
            </Badge>
            <Badge variant="light" color={data.contextConsent === "granted" ? "teal" : "gray"} tt="none" fw={500}>
              نمایش خدمت و متخصص: {consentLabels[data.contextConsent ?? ""] || "نامشخص"}
            </Badge>
          </Group>
        </Stack>
      </Card>

      {/* Where this review is in the flow */}
      <Stepper active={activeStep} size={isSmall ? "xs" : "sm"} allowNextStepsSelect={false}>
        <Stepper.Step
          label="بررسی"
          description={isSmall ? undefined : moderationLabels[data.moderationStatus ?? ""] || "نامشخص"}
          color={rejected ? "red" : undefined}
          icon={rejected ? <IconX size={16} /> : undefined}
        />
        <Stepper.Step label={isSmall ? "انتشار" : "انتشار در سایت"} description={isSmall ? undefined : visibilityLabels[data.visibilityStatus ?? ""] || "—"} />
        <Stepper.Step label={isSmall ? "پاسخ" : "پاسخ مجموعه"} description={isSmall ? undefined : replyLabels[replyState] || "بدون پاسخ"} />
      </Stepper>

      {mutation.isError && <Alert color="red">{reviewError(mutation.error)}</Alert>}
      {mutation.isSuccess && <Alert color="green">تغییر با موفقیت ثبت شد.</Alert>}

      {/* Step 1: approve or reject */}
      <Card withBorder radius="md" padding="md" style={activeStep === 0 ? activeCard : undefined}>
        <Stack gap="xs">
          <Group justify="space-between">
            <Text fw={700}>۱. بررسی نظر</Text>
            <Badge color={moderationColor(data.moderationStatus)} variant="light">
              {moderationLabels[data.moderationStatus ?? ""] || "نامشخص"}
            </Badge>
          </Group>
          {data.moderationReasonText && (
            <Text size="sm" c="red.7">دلیل رد: {data.moderationReasonText}</Text>
          )}
          {canModerate && form !== "reject" && (
            <>
              {!moderated && (
                <Text size="sm" c="dimmed">
                  متن را بخوانید و در صورت مناسب بودن تأیید کنید. امتیاز پایین به‌تنهایی دلیل رد نیست.
                </Text>
              )}
              <Group>
                {actionAllowed("approve") && (
                  <Button color="teal" leftSection={<IconCheck size={16} />} loading={pendingAction === "approve"}
                    disabled={busy || !data.version} onClick={() => mutation.mutate("approve")}>
                    تأیید نظر
                  </Button>
                )}
                {actionAllowed("reject") && (
                  <Button color="red" variant="outline" leftSection={<IconX size={16} />} disabled={busy}
                    onClick={() => setForm("reject")}>
                    رد نظر
                  </Button>
                )}
              </Group>
            </>
          )}
          {!canModerate && !moderated && (
            <Text size="sm" c="dimmed">اجازه بررسی این نظر را ندارید.</Text>
          )}
          {form === "reject" && (
            <Stack gap="xs">
              <Select
                error={fieldErrors?.reasonCode?.join("، ")}
                label="دلیل رد نظر"
                placeholder="یک دلیل انتخاب کنید"
                value={reasonCode}
                onChange={setReasonCode}
                data={[
                  { value: "personalData", label: "اطلاعات شخصی" },
                  { value: "abuse", label: "توهین یا سوءاستفاده" },
                  { value: "spamOrUnrelated", label: "هرزنامه یا نامرتبط" },
                  { value: "other", label: "سایر" },
                ]}
                comboboxProps={{ zIndex: 400 }}
                disabled={busy}
              />
              <Textarea
                error={fieldErrors?.reasonText?.join("، ")}
                label={reasonCode === "other" ? "توضیح (الزامی)" : "توضیح (اختیاری)"}
                maxLength={reasonLimit}
                autosize
                minRows={2}
                value={reason}
                onChange={(e) => setReason(e.currentTarget.value)}
                disabled={busy}
              />
              <Text size="xs" c="dimmed">
                نظر حذف نمی‌شود؛ دلیل در تاریخچه می‌ماند و اگر منتشر شده باشد از سایت برداشته می‌شود.
              </Text>
              <Group>
                <Button color="red" loading={pendingAction === "reject"}
                  disabled={busy || !data.version || !reasonCode || (reasonCode === "other" && !reason.trim()) || reason.length > reasonLimit}
                  onClick={() => mutation.mutate("reject")}>
                  ثبت رد نظر
                </Button>
                <Button variant="subtle" color="gray" onClick={cancelForm} disabled={busy}>انصراف</Button>
              </Group>
            </Stack>
          )}
        </Stack>
      </Card>

      {/* Step 2: publish or hide on the salon site */}
      <Card withBorder radius="md" padding="md" style={activeStep === 1 ? activeCard : undefined}>
        <Stack gap="xs">
          <Group justify="space-between">
            <Text fw={700}>۲. نمایش در سایت</Text>
            <Badge color={visibilityColor(data.visibilityStatus)} variant="light">
              {visibilityLabels[data.visibilityStatus ?? ""] || "—"}
            </Badge>
          </Group>
          {!published && publishBlocker && <Text size="sm" c="dimmed">{publishBlocker}</Text>}
          {!published && !publishBlocker && actionAllowed("publish") && (
            <Text size="sm" c="dimmed">
              پس از انتشار، این نظر در سایت سالن دیده می‌شود. تأیید به‌تنهایی آن را منتشر نمی‌کند.
            </Text>
          )}
          {canVisibility && form !== "hide" && (
            <Group>
              {actionAllowed("publish") && (
                <Button color="green" leftSection={<IconWorld size={16} />} loading={pendingAction === "publish"}
                  disabled={busy || !data.version || !publishEligible} onClick={() => mutation.mutate("publish")}>
                  نمایش در سایت
                </Button>
              )}
              {actionAllowed("hide") && (
                <Button color="gray" variant="outline" leftSection={<IconEyeOff size={16} />} disabled={busy}
                  onClick={() => setForm("hide")}>
                  مخفی‌کردن از سایت
                </Button>
              )}
            </Group>
          )}
          {form === "hide" && (
            <Stack gap="xs">
              <Textarea
                error={fieldErrors?.reason?.join("، ")}
                label="دلیل مخفی‌کردن"
                placeholder="مثلاً به درخواست مشتری"
                maxLength={reasonLimit}
                autosize
                minRows={2}
                value={reason}
                onChange={(e) => setReason(e.currentTarget.value)}
                disabled={busy}
              />
              <Group>
                <Button color="gray" loading={pendingAction === "hide"} disabled={busy || !data.version || !reason.trim()}
                  onClick={() => mutation.mutate("hide")}>
                  مخفی شود
                </Button>
                <Button variant="subtle" color="gray" onClick={cancelForm} disabled={busy}>انصراف</Button>
              </Group>
            </Stack>
          )}
        </Stack>
      </Card>

      {/* Step 3: the salon's public reply */}
      <Card withBorder radius="md" padding="md" style={activeStep === 2 ? activeCard : undefined}>
        <Stack gap="xs">
          <Group justify="space-between">
            <Text fw={700}>۳. پاسخ مجموعه</Text>
            <Badge color={replied ? "green" : "gray"} variant="light">
              {replyLabels[replyState] || "بدون پاسخ"}
            </Badge>
          </Group>
          {data.reply?.publishedText && (
            <Alert color="green" variant="light" title="پاسخ منتشرشده">
              <Text size="sm" style={{ whiteSpace: "pre-wrap" }}>{data.reply.publishedText}</Text>
            </Alert>
          )}
          {canReply ? (
            <>
              <Textarea
                error={fieldErrors?.text?.join("، ")}
                placeholder="از مشتری تشکر کنید یا به نکته‌ای که گفته پاسخ دهید…"
                autosize
                minRows={3}
                maxLength={replyLimit}
                value={replyValue}
                onChange={(e) => setReply(e.currentTarget.value)}
                disabled={busy}
                description={`${replyValue.length.toLocaleString("fa-IR")} / ${replyLimit.toLocaleString("fa-IR")}`}
              />
              <Group>
                {actionAllowed("reply") && (
                  <Button loading={pendingAction === "reply"}
                    disabled={busy || !publishEligible || !replyValue.trim() || replyValue.length > replyLimit || (reply === null && !data.reply?.draftText)}
                    onClick={() => mutation.mutate("reply")}>
                    انتشار پاسخ
                  </Button>
                )}
                {actionAllowed("draft") && (
                  <Button variant="light" loading={pendingAction === "draft"}
                    disabled={busy || !replyValue.trim() || replyValue.length > replyLimit}
                    onClick={() => mutation.mutate("draft")}>
                    ذخیره پیش‌نویس
                  </Button>
                )}
              </Group>
              <Text size="xs" c="dimmed">
                {publishEligible
                  ? "پیش‌نویس در سایت نمایش داده نمی‌شود و پیامکی برای مشتری ارسال نمی‌شود."
                  : "انتشار پاسخ بعد از تأیید نظر و با اجازه مشتری ممکن است؛ تا آن زمان می‌توانید پیش‌نویس ذخیره کنید."}
              </Text>
            </>
          ) : !data.reply?.publishedText ? (
            <Text size="sm" c="dimmed">پاسخی ثبت نشده است.</Text>
          ) : null}
        </Stack>
      </Card>

      {caps.isError && (
        <Alert color="yellow">
          محدودیت‌های متن دریافت نشد.
          <Button size="xs" variant="subtle" onClick={() => void caps.refetch()}>تلاش مجدد</Button>
        </Alert>
      )}

      {/* Disabled for now: review SMS status and change history (not rendered, not requested).
      <Accordion variant="separated" radius="md">
        {data.currentPurchaseId ? (
          <Accordion.Item value="sms">
            <Accordion.Control>پیامک دریافت نظر</Accordion.Control>
            <Accordion.Panel>
              <InvitationStatus purchaseId={data.currentPurchaseId} />
            </Accordion.Panel>
          </Accordion.Item>
        ) : null}
        {(data.audit?.length ?? 0) > 0 && (
          <Accordion.Item value="audit">
            <Accordion.Control>
              تاریخچه تغییرات ({(data.audit?.length ?? 0).toLocaleString("fa-IR")})
            </Accordion.Control>
            <Accordion.Panel>
              <Timeline bulletSize={14} lineWidth={2}>
                {data.audit?.map((entry, index) => (
                  <Timeline.Item key={index} title={<Text size="sm">{auditLabel(entry)}</Text>}>
                    <Text size="xs" c="dimmed">
                      {entry.occurredAtUtc ? moment(entry.occurredAtUtc).format("jYYYY/jMM/jDD HH:mm") : ""}
                    </Text>
                  </Timeline.Item>
                ))}
              </Timeline>
            </Accordion.Panel>
          </Accordion.Item>
        )}
      </Accordion>
      */}
    </Stack>
  );
}
